/**
 * RBAC di Kipo — ruoli dello spazio di lavoro e matrice dei permessi.
 * Unica fonte di verità: l'API la applica, il frontend la usa per la UI.
 * I permessi dei moduli sono dichiarati anche nei manifest (modules.ts).
 */

/** client = cliente esterno: vede solo i progetti a cui è invitato (richieste, avanzamento, file). */
export const ROLES = ["owner", "admin", "manager", "user", "client"] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  // nucleo
  "tenant:read",
  "tenant:update",
  "tenant:billing",
  "members:read",
  "members:invite",
  "members:manage",
  "modules:manage",
  "audit:read",
  "api_keys:manage",
  // persone: profili e capacità, costi aziendali (dati riservati)
  "people:read",
  "people:manage",
  "people:costs",
  "documents:read",
  "documents:write",
  "mailbox:read",
  "mailbox:draft",
  "email_log:read",
  // anagrafiche condivise
  "companies:read",
  "companies:write",
  "contacts:read",
  "contacts:write",
  // Progetti
  "projects:read",
  "projects:write",
  "projects:manage",
  "projects:billing",
  // CRM
  "deals:read",
  "deals:read_all",
  "deals:write",
  "activities:read",
  "activities:write",
  "settings:pipelines",
  // Vendite e finanza
  "quotes:read",
  "quotes:write",
  "invoices:read",
  "invoices:write",
  "invoices:send_sdi",
  "finance:read",
  "finance:write",
  // Commesse
  "contracts:read",
  "contracts:write",
  "contracts:economics",
  // Spese
  "expenses:read",
  "expenses:write",
  "expenses:approve",
  "expenses:read_all",
  // Marketing
  "marketing:read",
  "marketing:write",
  // AI
  "ai:use",
  "ai:configure",
  // report
  "reports:read",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ALL = [...PERMISSIONS] as Permission[];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: ALL,
  admin: ALL.filter((p) => p !== "tenant:billing"),
  manager: [
    "tenant:read",
    "members:read",
    "people:read",
    "mailbox:read",
    "mailbox:draft",
    "email_log:read",
    "documents:read",
    "documents:write",
    "companies:read",
    "companies:write",
    "contacts:read",
    "contacts:write",
    "projects:read",
    "projects:write",
    "projects:manage",
    "projects:billing",
    "deals:read",
    "deals:read_all",
    "deals:write",
    "activities:read",
    "activities:write",
    "quotes:read",
    "quotes:write",
    "invoices:read",
    "finance:read",
    "contracts:read",
    "contracts:write",
    "contracts:economics",
    "expenses:read",
    "expenses:write",
    "expenses:approve",
    "expenses:read_all",
    "marketing:read",
    "marketing:write",
    "ai:use",
    "reports:read",
  ],
  user: [
    "tenant:read",
    "members:read",
    "documents:read",
    "documents:write",
    "companies:read",
    "companies:write",
    "contacts:read",
    "contacts:write",
    "projects:read",
    "projects:write",
    "deals:read",
    "deals:write",
    "activities:read",
    "activities:write",
    "quotes:read",
    "quotes:write",
    "expenses:read",
    "expenses:write",
    "marketing:read",
    "ai:use",
    "reports:read",
  ],
  client: ["tenant:read", "projects:read", "projects:write"],
};

export function roleHas(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Ruoli che un ruolo può assegnare (nessuno tocca l'owner). */
export const MANAGEABLE_ROLES: Record<Role, readonly Role[]> = {
  owner: ["admin", "manager", "user", "client"],
  admin: ["manager", "user", "client"],
  manager: [],
  user: [],
  client: [],
};

/** Ruoli dentro un singolo progetto (da Planner). */
export const PROJECT_ROLES = ["MAINTAINER", "DEVELOPER", "CLIENT"] as const;
export type ProjectRole = (typeof PROJECT_ROLES)[number];
